import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/Reveal";
import { CONTACT } from "@/data/site";
import { cn } from "@/lib/utils";

export const mailto = (subject) =>
  `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}`;

// Hero-lite: off-white + teal wash, title/subtitle/breadcrumb, image on right at md+.
export const PageHero = ({ title, subtitle, image, imageAlt, accent = "teal" }) => {
  const wash =
    accent === "brown"
      ? "from-rda-brown/15 via-rda-offwhite to-rda-offwhite"
      : "from-rda-teal/15 via-rda-offwhite to-rda-offwhite";
  return (
    <section
      className={cn("relative bg-gradient-to-br", wash)}
      data-testid="page-hero"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20 min-h-[40vh] flex items-center">
        <div className="grid w-full items-center gap-10 md:grid-cols-2">
          <Reveal>
            <nav className="flex items-center gap-1.5 text-sm text-rda-muted" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-rda-teal transition-colors" data-testid="breadcrumb-home">
                Home
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-rda-ink font-medium">{title}</span>
            </nav>
            <h1 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl text-rda-ink leading-[1.05]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-5 text-lg text-rda-muted max-w-lg">{subtitle}</p>
            )}
          </Reveal>
          {image && (
            <Reveal delay={0.1} className="hidden md:block">
              <div className="overflow-hidden rounded-3xl shadow-lift">
                <img src={image} alt={imageAlt} loading="lazy" className="h-80 w-full object-cover" />
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

// Narrow prose container for long-form body copy.
export const Prose = ({ children, className = "" }) => (
  <section className={cn("bg-rda-offwhite", className)}>
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <Reveal>
        <div className="prose-rda space-y-5 text-rda-ink/85 text-[17px] leading-relaxed">
          {children}
        </div>
      </Reveal>
    </div>
  </section>
);

export const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="max-w-2xl">
    {eyebrow && (
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rda-teal">
        {eyebrow}
      </p>
    )}
    <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl text-rda-ink">
      {title}
    </h2>
    {children && <p className="mt-3 text-rda-muted">{children}</p>}
  </div>
);

// Alternating full-width feature band (image left/text right, or reversed).
export const FeatureBand = ({ image, imageAlt, title, children, reverse = false, dark = false }) => (
  <section className={dark ? "bg-white" : "bg-rda-offwhite"}>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <div className="grid items-center gap-10 md:grid-cols-2">
        <Reveal className={reverse ? "md:order-2" : ""}>
          <div className="overflow-hidden rounded-3xl shadow-soft">
            <img src={image} alt={imageAlt} loading="lazy" className="h-72 md:h-96 w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.08} className={reverse ? "md:order-1" : ""}>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-rda-ink">
            {title}
          </h2>
          <div className="mt-4 space-y-4 text-rda-ink/80 leading-relaxed">{children}</div>
        </Reveal>
      </div>
    </div>
  </section>
);

// Teal mid-page CTA band. `href` renders a link (e.g. mailto), otherwise pass custom `action`.
export const CtaBand = ({ title, subtitle, ctaLabel, href, action, testId = "page-cta-band" }) => (
  <section className="bg-rda-teal" data-testid={testId}>
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
      <Reveal>
        <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-white">{title}</h2>
        {subtitle && <p className="mt-4 text-white/85 max-w-xl mx-auto">{subtitle}</p>}
        <div className="mt-8 flex justify-center">
          {action ? (
            action
          ) : (
            <Button
              asChild
              size="lg"
              className="rounded-full bg-white text-rda-tealDark hover:bg-white/90 font-semibold px-7 h-12"
              data-testid={`${testId}-cta`}
            >
              <a href={href}>
                <Mail className="mr-2 h-4 w-4" /> {ctaLabel}
              </a>
            </Button>
          )}
        </div>
      </Reveal>
    </div>
  </section>
);

// 2–3 card strip linking to sibling pages.
export const RelatedLinks = ({ links }) => (
  <section className="bg-white border-t border-border" data-testid="related-links">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <SectionHeading title="Explore more" />
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {links.map((l, i) => (
          <Reveal key={l.to} delay={i * 0.08}>
            <Link
              to={l.to}
              className="group flex h-full flex-col rounded-2xl border border-border bg-rda-offwhite p-7 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
              data-testid={`related-link-${l.to.slice(1)}`}
            >
              <h3 className="font-heading font-bold text-lg text-rda-ink">{l.title}</h3>
              <p className="mt-2 text-sm text-rda-muted flex-1">{l.description}</p>
              <span className="mt-5 inline-flex items-center text-sm font-semibold text-rda-teal">
                Read more
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// Reusable icon-card grid.
export const IconCardGrid = ({ cards, cols = 3 }) => (
  <div
    className={cn(
      "grid gap-6",
      cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"
    )}
  >
    {cards.map((card, i) => (
      <Reveal key={card.title} delay={i * 0.07}>
        <div className="group h-full rounded-2xl border border-border bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal transition-colors group-hover:bg-rda-teal group-hover:text-white">
            <card.Icon className="h-6 w-6" />
          </div>
          <h3 className="mt-5 font-heading font-bold text-lg text-rda-ink">{card.title}</h3>
          <p className="mt-2 text-sm text-rda-muted leading-relaxed">{card.body}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

// Prominent trustees appeal callout (shared across pages).
export const TrusteesCallout = () => (
  <section className="bg-rda-offwhite" data-testid="trustees-callout">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <Reveal>
        <div className="rounded-3xl border border-rda-brown/25 bg-rda-brown/10 p-8 md:p-12 md:flex md:items-center md:justify-between md:gap-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-rda-brown px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
              We need you
            </span>
            <h2 className="mt-4 font-heading font-extrabold text-2xl md:text-3xl text-rda-ink">
              Charity Trustees Required
            </h2>
            <p className="mt-3 text-rda-ink/80 leading-relaxed">
              We're looking for enthusiastic new trustees to help guide the future of
              RDA Glasgow. Share your skills and help more disabled people experience
              the benefits of equine therapy.
            </p>
          </div>
          <div className="mt-6 md:mt-0 shrink-0">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-rda-brown text-white hover:bg-rda-brown/90 font-semibold px-7 h-12"
              data-testid="trustees-callout-cta"
            >
              <Link to="/vacancies">
                Learn more <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
